import { isNil, isNotNil } from 'ramda'

import { TableStyles } from '~/src/components/generic/DraggableTable/types'

import {
  getFreightStatus,
  getLocalTime,
  isLayoverStop,
} from '~/src/components/shared/features/helpers'
import {
  DEFAULT_TIME_ZONE_CODE,
  TWENTY_FOUR_HOURS_IN_MILLISECONDS,
} from '~/src/components/shared/features/constants'
import { StopStatus } from '~/src/components/shared/features/types'

import { roundNumber } from '~/helpers/number'
import { prettyDateTimeFromObj } from '~/helpers/date'
import { endOfDay, endOfDayMoment, startOfDay, startOfDayMoment } from '~/helpers/time'
import { enumToPretty } from '~/helpers/string'

import { SearchType } from '~/src/components/Operational/LoadBoardDashboard/hooks/types'

import { UNAVAILABLE_TRACTOR_STATUSES } from '~/src/components/Operational/DispatchLoadDashboard/constants'

import {
  LegStatus,
  LoadStatus,
  LoadTenderStatus,
  ScosStopAction,
  StopStatus as Status,
} from '~/__generated_types__/globalTypes'

import {
  DetailedLoad,
  DetailedLoadCore,
  FreightRecord,
  LegWithEstimates,
  Load,
  LoadCostCenter,
  LoadWithScheduleEstimates,
  ReOrderableStop,
  Stop,
  StopType,
  StopWithEstimate,
  StopUpdateBase,
  Tractor,
  LoadForAutoDispatch,
  LegForAutoDispatch,
} from '~/src/components/Operational/LoadBoardDashboard/types'

/**
 * helper function to get the date range for the current week
 */
const getWeekDateRange = ({
  startOffset = 7,
  endOffset = 0,
}: {
  startOffset?: number
  endOffset?: number
}): { start: string; end: string } => {
  const today = new Date()
  const startDateTime = startOfDay(today)
  const endDateTime = endOfDay(today)
  const startDate = new Date(startDateTime.setDate(startDateTime.getDate() - endOffset))
  const endDate = new Date(endDateTime.setDate(endDateTime.getDate() + startOffset))

  return { start: startDate.toISOString(), end: endDate.toISOString() }
}

const getFormattedTime = (time: string | undefined | null): string | undefined => {
  return time
    ? prettyDateTimeFromObj(new Date(time), false, { weekday: 'short' })
    : undefined
}

const formatTime = (time: string): string => {
  const [hours, minutes] = time.split(':')
  const hoursStr = Number(hours) === 1 ? 'hr' : 'hrs'
  const minutesStr = Number(minutes) === 1 ? 'min' : 'mins'

  if (hours === '00') {
    return `${minutes} ${minutesStr}`
  }

  if (minutes === '00') {
    return `${hours} ${hoursStr}`
  }

  return `${hours} ${hoursStr},  ${minutes} ${minutesStr}`
}

const getTrackingData = (
  load:
    | {
        status?: LoadStatus | null
        ryderNumber?: string | null
        costCenter?: {
          name: string
          code?: string | null
          radiusVersion: string
        } | null
      }
    | undefined
): {
  version: string
  tmwOrder: string | undefined | null
  costCenterName: string | undefined | null
  costCenterCode: string | undefined | null
  loadStatus: string | undefined
  ryderNumber: string | undefined | null
} => {
  return {
    version: `Radius ${load?.costCenter?.radiusVersion}`,
    tmwOrder: load?.ryderNumber,
    costCenterName: load?.costCenter?.name,
    costCenterCode: load?.costCenter?.code,
    loadStatus: enumToPretty(load?.status),
    ryderNumber: load?.ryderNumber,
  }
}

const getAutoDispatchEligible = (load: {
  targetDispatchStartInterval?: number | null
  isRv3?: boolean | null
  tenderStatus: LoadTenderStatus
  legs: Array<{ carrierDetails: { carrier?: { id: number } | null } }>
}): boolean => {
  return (
    isNotNil(load.targetDispatchStartInterval) &&
    Boolean(load.isRv3) &&
    load.tenderStatus === LoadTenderStatus.Tendered &&
    !load.legs[0]?.carrierDetails.carrier
  )
}

const canStopBeAdded = (
  stops: Array<Stop>,
  stop: Stop,
  costCenter: LoadCostCenter | undefined | null,
  equipmentMoveFeatureFlag = false
): Array<boolean> => {
  if (!costCenter?.id) {
    return [false, false]
  }

  if (
    equipmentMoveFeatureFlag &&
    costCenter.radiusVersion === 'V3' &&
    stop.type === 'EQMV'
  ) {
    return [false, stop.idx === stops.length || stops[stop.idx].type !== 'EQMV']
  }

  if (costCenter.radiusVersion === 'V3') {
    return [true, true]
  }

  let canBeAddedBefore = true
  let canBeAddedAfter = true

  if (stop.arrivalTime || stop.departureTime) {
    canBeAddedBefore = false
  }

  const stopsAheadWithArrivalOrDepartureTimes = stops
    .slice(stop.idx)
    .some((stop) => stop.arrivalTime || stop.departureTime)

  if (stopsAheadWithArrivalOrDepartureTimes) {
    canBeAddedAfter = false
  }

  const isExecutionComplete = stops.every(
    (stop) => stop.arrivalTime && stop.departureTime
  )

  if (isExecutionComplete) {
    canBeAddedBefore = false
    canBeAddedAfter = false
  }

  return [canBeAddedBefore, canBeAddedAfter]
}

const getStopStatus = (
  stop: Stop,
  stops: Array<Stop>,
  legStatus: LegStatus
): StopStatus | null => {
  if (legStatus === LegStatus.Cancelled) {
    return StopStatus.Cancelled
  }

  if (stop.status === Status.Skipped) {
    return StopStatus.Skipped
  }

  if (stop.status === Status.Attempted) {
    return StopStatus.Attempted
  }

  if (stop.arrivalTime && stop.departureTime) {
    return StopStatus.Completed
  }

  const planned_statuses = [
    LegStatus.Planned,
    LegStatus.Available,
    LegStatus.Dispatched,
  ]

  if (stop.idx === 1) {
    if (legStatus === LegStatus.Started) {
      return StopStatus.AtLocation
    } else if (planned_statuses.includes(legStatus)) {
      return StopStatus.Planned
    }
  } else {
    if (stop.arrivalTime && !stop.departureTime) {
      return StopStatus.AtLocation
    } else if (
      stops[stop.idx - 2].departureTime &&
      !stop.arrivalTime &&
      stop.departureTime
    ) {
      return StopStatus.Completed
    } else if (
      stops[stop.idx - 2].departureTime &&
      !stop.arrivalTime &&
      !stop.departureTime
    ) {
      return StopStatus.InTransit
    } else if (!stops[stop.idx - 2].departureTime) {
      return StopStatus.Planned
    }
  }

  return null
}

const getStopFreightStatus = (
  freight: Pick<FreightRecord, 'osdRecord'>,
  stop: Stop,
  stops: Array<Stop>,
  legStatus: LegStatus
) => {
  const stopStatus = getStopStatus(stop, stops, legStatus)
  const osdRecordStatus = freight.osdRecord?.status

  return getFreightStatus(stopStatus, osdRecordStatus, stop.scosAction)
}

const getStopTimeZone = (stop: Stop, leg: LegWithEstimates) => {
  let stopLocation = stop.location

  if (isLayoverStop(stop) && !stop.location) {
    stopLocation = leg.stops[stop.idx - 2].location
  }

  const { timeZoneName, timeZoneCode = DEFAULT_TIME_ZONE_CODE } = stopLocation || {}

  return {
    timeZoneName,
    timeZoneCode,
  }
}

const getTableStyles = (errorRow: Stop | null): TableStyles<Stop> => {
  return {
    rows: {
      style:
        'h-[6.5rem] items-center text-body-s gap-x-4 [&:not(:last-child)]:border-b border-x border-divider group hover:bg-gray-100',
      getHighlightOnSelectStyle: (row) => {
        if (row.id === errorRow?.id) {
          return 'border-red-500 border'
        }
        if (row.status === Status.Skipped) return '[&_*]:text-secondary'

        return ''
      },
    },
    header: {
      style:
        'h-8 justify-start items-center inline-flex text-label-xs uppercase bg-gray-100 text-default gap-x-4 border border-gray-200',
    },
    headerCell: {
      style: 'px-0',
    },
    cells: {
      style: 'px-0',
    },
  }
}

const getStopsWithReorderStatusRadiusV2 = (
  leg: LegWithEstimates
): Array<ReOrderableStop> => {
  return leg.stops.map((stop) => {
    const status = getStopStatus(stop, leg.stops, leg.status)

    return {
      ...stop,
      legId: leg.id,
      isReorderable: [StopStatus.Planned, StopStatus.InTransit].includes(
        status as StopStatus
      ),
    }
  })
}

const getStopsWithReorderStatusRadiusV3 = (
  leg: LegWithEstimates
): Array<ReOrderableStop> => {
  return leg.stops.map((stop) => {
    const status = getStopStatus(stop, leg.stops, leg.status)

    return {
      ...stop,
      legId: leg.id,
      isReorderable:
        [StopStatus.Planned, StopStatus.InTransit].includes(status as StopStatus) &&
        stop.type !== 'EQMV',
    }
  })
}

const isLegPlannedStartTimeEditable = (
  load: DetailedLoad | undefined,
  legIdx: number,
  isRV3?: boolean | null
) => {
  if (!load) return false

  const currentLeg = load.legs.at(legIdx - 1)

  if (!currentLeg) return false

  const isLastLeg = load.legs.length === currentLeg.idx
  const isPreviousLegCompleted =
    currentLeg.idx === 1 || load.legs[currentLeg.idx - 2].status === LegStatus.Completed
  const isRV2 = isLastLeg && isPreviousLegCompleted

  return (
    [LegStatus.Available, LegStatus.Planned, LegStatus.Dispatched].includes(
      currentLeg.status
    ) &&
    (isRV3 || isRV2)
  )
}

const mergeDetailedLoadCore = (
  currentLoad: DetailedLoad,
  incomingLoad: DetailedLoadCore
): DetailedLoad => {
  const currentLegById = new Map(currentLoad.legs.map((leg) => [leg.id, leg]))
  const currentStopById = new Map(
    currentLoad.legs.flatMap((leg) => leg.stops.map((stop) => [stop.id, stop]))
  )

  return {
    ...currentLoad,
    ...incomingLoad,
    legs: incomingLoad.legs.map((incomingLeg) => {
      const currentLeg = currentLegById.get(incomingLeg.id)

      if (!currentLeg) {
        return incomingLeg as LegWithEstimates
      }

      return {
        ...currentLeg,
        ...incomingLeg,
        stops: incomingLeg.stops.map((incomingStop) => {
          const currentStop = currentStopById.get(incomingStop.id)

          if (!currentStop) {
            return incomingStop as Stop
          }

          return {
            ...currentStop,
            ...incomingStop,
          }
        }),
      }
    }),
  }
}

const transformData = (load: DetailedLoad) => {
  let idx = 1

  const removeMissingDocuments = (documents: DetailedLoad['documents']) => {
    if (documents.__typename === 'DocumentListWithMissing') {
      return {
        ...documents,
        documents: documents.documents.filter(
          (document) => document.__typename !== 'MissingDocument'
        ),
      }
    } else {
      return documents
    }
  }

  return {
    ...load,
    legs: load.legs.map((leg) => {
      return {
        ...leg,
        stops: leg.stops.map((stop) => {
          return {
            ...stop,
            globalIdx: stop.status === Status.Skipped ? -1 : idx++,
          }
        }),
      }
    }),
    documents: removeMissingDocuments(load.documents),
    exceptions: load.exceptions.filter(
      (exception) => exception.__typename !== 'MissingDocumentException'
    ),
  }
}

const getApptTime = (stop: Stop | undefined, load: DetailedLoad) => {
  const timeZoneName = stop?.location?.timeZoneName

  const { suggestedStartTime, suggestedEndTime } = getSuggestedAppointmentWindow(
    load,
    timeZoneName
  )

  const startTime = stop?.isAppointmentValid
    ? getLocalTime(stop.appointmentStart, timeZoneName)
    : suggestedStartTime.time

  const endTime = stop?.isAppointmentValid
    ? getLocalTime(stop.appointmentEnd, timeZoneName)
    : suggestedEndTime.time

  return {
    startTime,
    endTime,
  }
}

const validateAppointmentTime = (
  appointmentStart: string | undefined | null,
  appointmentEnd: string | undefined | null
): { message: string | undefined } => {
  if (isNil(appointmentStart) || isNil(appointmentEnd)) {
    return { message: 'Provide complete appointment start and end times.' }
  }

  const appTimeStart = new Date(appointmentStart).toISOString()
  const appTimeEnd = new Date(appointmentEnd).toISOString()

  let message: string | undefined = undefined

  if (appTimeStart > appTimeEnd) {
    message = 'Appointment start time can’t be after end time'
  }

  return { message }
}

const getSuggestedAppointmentWindow = (
  load: DetailedLoad | undefined,
  timeZoneName: string | undefined
): {
  suggestedStartTime: {
    time: string | undefined
    timeZone: string | undefined
  }
  suggestedEndTime: {
    time: string | undefined
    timeZone: string | undefined
  }
} => {
  const firstLeg = load?.legs[0]
  const lastLeg = load?.legs.at(-1)

  const lastStopDeparture =
    lastLeg?.stops.at(-1)?.departureTime ??
    lastLeg?.stops.at(-1)?.estimateData?.estimatedDepartureTime

  const localTimeAppointmentStart = getLocalTime(
    firstLeg?.plannedStartTime,
    timeZoneName
  )
  const localTimeAppointmentEnd = getLocalTime(
    lastLeg?.plannedEndTime ?? lastStopDeparture,
    timeZoneName
  )

  const appointmentStart =
    localTimeAppointmentStart && startOfDayMoment(localTimeAppointmentStart)
  const appointmentEnd =
    localTimeAppointmentEnd && endOfDayMoment(localTimeAppointmentEnd)

  return {
    suggestedStartTime: {
      time: appointmentStart,
      timeZone: timeZoneName,
    },
    suggestedEndTime: {
      time: appointmentEnd,
      timeZone: timeZoneName,
    },
  }
}

const isAppointmentTimeChanged = (stopUpdate: StopUpdateBase, stop: Stop) => {
  const isAppointmentStartChanged =
    stopUpdate.appointmentStart &&
    (!stop.appointmentStart || stopUpdate.appointmentStart !== stop.appointmentStart)

  const isAppointmentEndChanged =
    stopUpdate.appointmentEnd &&
    (!stop.appointmentEnd || stopUpdate.appointmentEnd !== stop.appointmentEnd)

  return isAppointmentStartChanged || isAppointmentEndChanged
}

/**
 * Get in progress or next stop which is the stop that has either
 * estimated arrival time or estimated departure time.  Completed stops
 * do not have estimated times associated with them.
 */
const getCurrentOrNextStop = (
  load: DetailedLoad | LoadWithScheduleEstimates | undefined
): Stop | StopWithEstimate | undefined => {
  const allStops = load?.legs.flatMap((leg) => leg.stops)

  return allStops?.find(
    (stop) =>
      stop.estimateData?.estimatedArrivalTime ||
      stop.estimateData?.estimatedDepartureTime
  )
}

/**
 * Determine whether the provided expiration date is before the planned end date.
 */
const isExpirationDateBeforePlannedEnd = (
  expirationDateString: string,
  plannedEndDateString: string
): boolean => {
  return new Date(expirationDateString) < new Date(plannedEndDateString)
}

const extractLocationInfo = (legs: Array<LegWithEstimates>) => {
  const firstLeg = legs?.[0]
  const lastLeg = legs?.[legs.length - 1]
  const origin = firstLeg?.origin
  const destination = firstLeg?.destination

  const startTimeZoneInfo = firstLeg?.stops?.[0]?.location
  const endTimeZoneInfo = lastLeg?.stops?.at(-1)?.location

  return {
    origin,
    destination,
    startTimeZoneName: startTimeZoneInfo?.timeZoneName,
    startTimeZoneCode: startTimeZoneInfo?.timeZoneCode,
    endTimeZoneName: endTimeZoneInfo?.timeZoneName,
    endTimeZoneCode: endTimeZoneInfo?.timeZoneCode,
  }
}

const getRemainingTime = (scheduledTime: string): number => {
  const lastStopTime = new Date(scheduledTime)

  // Calculate 24 hours after scheduled time
  const lastStopPlus24Hours = new Date(
    lastStopTime.getTime() + TWENTY_FOUR_HOURS_IN_MILLISECONDS
  )

  const currentTime = new Date()

  if (currentTime < lastStopTime) {
    const timeUntilScheduled = lastStopTime.getTime() - currentTime.getTime()

    return Math.floor(timeUntilScheduled / 1000)
  }

  const differenceInMilliseconds = lastStopPlus24Hours.getTime() - currentTime.getTime()

  if (differenceInMilliseconds <= 0) {
    return 0
  }

  return Math.floor(differenceInMilliseconds / 1000)
}

/**
 * @param ignoreTractorUnavailability - When true, tractors are not sorted by unavailable status;
 *   used for FMS business unit assignment modals. Trailer lists use `ignoreTrailerUnavailability`
 *   in Modals/helpers; consider extracting FMS-specific availability instead of threading booleans.
 */
const sortTractorsByAvailability = ({
  tractors,
  licenseExpiryValidationEnabled,
  ignoreTractorUnavailability = false,
}: {
  tractors: Array<Tractor>
  licenseExpiryValidationEnabled: boolean
  ignoreTractorUnavailability?: boolean
}) => {
  if (!licenseExpiryValidationEnabled && !ignoreTractorUnavailability) {
    return [...tractors].sort(sortTractorsByStatus)
  } else {
    return [...tractors].sort(sortTractorsByLicenseExpiry())
  }
}

const sortTractorsByLicenseExpiry =
  () => (tractorLeft: Tractor, tractorRight: Tractor) => {
    const tractorLeftExpired = isTractorExpired(tractorLeft)
    const tractorRightExpired = isTractorExpired(tractorRight)

    if (tractorLeftExpired && !tractorRightExpired) return 1
    if (!tractorLeftExpired && tractorRightExpired) return -1

    return 0
  }

/**
 * @param ignoreTractorUnavailability - When true, the tractor is never treated as unavailable
 *   in this UI (FMS business unit flow). Pairs with {@link sortTractorsByAvailability}; trailer
 *   assignment uses `ignoreTrailerUnavailability` in Modals/helpers. Split FMS rules out in fast follow.
 */
const isTractorUnavailable = (
  tractor: Tractor,
  licenseExpiryValidationEnabled: boolean,
  ignoreTractorUnavailability: boolean
): boolean => {
  if (ignoreTractorUnavailability) {
    return false
  }

  if (licenseExpiryValidationEnabled) {
    return isTractorExpired(tractor)
  }

  return UNAVAILABLE_TRACTOR_STATUSES.includes(tractor.status)
}

const sortTractorsByStatus = (tractorLeft: Tractor, tractorRight: Tractor) => {
  if (
    UNAVAILABLE_TRACTOR_STATUSES.includes(tractorLeft.status) &&
    !UNAVAILABLE_TRACTOR_STATUSES.includes(tractorRight.status)
  ) {
    return 1
  }
  if (
    !UNAVAILABLE_TRACTOR_STATUSES.includes(tractorLeft.status) &&
    UNAVAILABLE_TRACTOR_STATUSES.includes(tractorRight.status)
  ) {
    return -1
  }

  return 0
}

const isTractorExpired = (tractor: Tractor): boolean => {
  return isNotNil(tractor.licenseExpiryDate)
    ? new Date(tractor.licenseExpiryDate) < new Date()
    : false
}

const getLegsWithoutDriverIndices = (
  load: DetailedLoad | Load,
  isRV3: boolean
): Array<number> => {
  const legs = isRV3 ? [load.legs[0]] : load.legs

  return legs.filter((leg) => !leg.primaryDriver).map((leg) => leg.idx)
}

const filterOutSkippedStops = (stops: Array<Stop>): Array<Stop> => {
  return stops.filter((stop) => stop.status !== Status.Skipped)
}

const findFirstMatchingValue = (
  load: Load,
  value: string | undefined
): SearchType | undefined => {
  const barcodeMatch = load.freightDetails?.freightRecords?.find(
    (freightRecord) => freightRecord.osdRecord?.barcode === value
  )

  if (barcodeMatch?.osdRecord) {
    return SearchType.FreightRecordBarcode
  }

  const freightReferenceMatch = load.freightRecordReferences?.find(
    (freightRecordReference) => freightRecordReference.value === value
  )

  if (freightReferenceMatch) {
    return SearchType.FreightRecordReference
  }

  return undefined
}

const getTotalArrivedStopsForLeg = (leg: LegWithEstimates | undefined): number => {
  return leg?.stops.filter((stop) => isNotNil(stop.arrivalTime)).length ?? 0
}

export const isAddableStopType = (stopType: StopType): boolean => {
  if (stopType.code === 'EQMV') {
    return false
  }

  if (stopType.code === 'LAYO') {
    return false
  }

  return true
}

export const isEditableStopType = (stop: Stop | undefined | null): boolean => {
  if (isNil(stop)) {
    return false
  }

  if (stop.status === Status.Skipped) {
    return false
  }

  if (stop.type === 'EQMV') {
    return false
  }

  if (isLayoverStop(stop)) {
    return false
  }

  return true
}

export const isEditableStopEvent = (stop: Stop | undefined | null): boolean => {
  if (isNil(stop)) {
    return false
  }

  if (stop.status === Status.Skipped) {
    return false
  }

  if (isLayoverStop(stop)) {
    return false
  }

  return true
}

export const isStopLocationEditable = (stop: Stop | undefined | null): boolean => {
  if (isNil(stop)) {
    return false
  }

  if (stop.status === Status.Skipped) {
    return false
  }

  if (stop.type === 'EQMV') {
    return false
  }

  if (isLayoverStop(stop) && !stop.location) {
    return false
  }

  return true
}

const calculateMileageForStop = (
  stopIdx: number,
  stops: Array<Partial<Stop>>,
  startingOdometer: number
): number => {
  if (stopIdx === 1) {
    return roundNumber(startingOdometer, 0)
  }

  let currentMileage = startingOdometer

  for (let i = 0; i < stops.length; i++) {
    const currStop = stops[i]

    if (currStop.idx === 1) {
      currentMileage =
        currStop.tractorMileage && currStop.tractorMileage !== 0
          ? currStop.tractorMileage
          : startingOdometer
    } else if (currStop.tractorMileage && currStop.tractorMileage !== 0) {
      currentMileage = currStop.tractorMileage
    } else {
      const milesFromPrev = currStop.estimateData?.estimatedMilesFromPrevStop ?? 0

      currentMileage = currentMileage + milesFromPrev
    }

    if (currStop.idx === stopIdx) {
      return roundNumber(currentMileage, 0)
    }
  }

  return 0
}

const willLoadBeImmediatelyDispatched = (
  load: LoadForAutoDispatch,
  leg: LegForAutoDispatch
): boolean => {
  // feature disabled
  if (isNil(load.targetDispatchStartInterval)) {
    return false
  }

  if (![LoadStatus.Available, LoadStatus.Planned].includes(load.status)) {
    return false
  }

  if (load.tenderStatus !== LoadTenderStatus.Tendered) {
    return false
  }

  if (leg.idx !== 1) {
    return false
  }

  if (isNil(leg.plannedStartTime)) {
    return false
  }

  if (isNil(leg.primaryDriver)) {
    return false
  }

  return (
    new Date(leg.plannedStartTime).getTime() -
      load.targetDispatchStartInterval * 1_000 <=
    Date.now()
  )
}

const getImmediateDispatchReason = (
  load: LoadForAutoDispatch,
  leg: LegForAutoDispatch
): 'planned_start_in_window' | 'planned_start_in_past' | undefined => {
  if (!willLoadBeImmediatelyDispatched(load, leg)) return undefined
  if (isNil(leg.plannedStartTime)) return undefined

  return new Date(leg.plannedStartTime).getTime() <= Date.now()
    ? 'planned_start_in_past'
    : 'planned_start_in_window'
}

const isHookStop = (stop: { scosAction?: ScosStopAction | null }) => {
  if (!stop.scosAction) return false

  return [
    ScosStopAction.Hlt,
    ScosStopAction.Ihlt,
    ScosStopAction.Hmt,
    ScosStopAction.Ihmt,
    ScosStopAction.Hct,
    ScosStopAction.Ihct,
    ScosStopAction.Hpl,
  ].includes(stop.scosAction)
}

const isDropStop = (stop: { scosAction?: ScosStopAction | null }) => {
  if (!stop.scosAction) return false

  return [
    ScosStopAction.Dlt,
    ScosStopAction.Idlt,
    ScosStopAction.Dmt,
    ScosStopAction.Idmt,
    ScosStopAction.Drl,
    ScosStopAction.Dtw,
  ].includes(stop.scosAction)
}

const isDropOrHookStop = (stop: { scosAction?: ScosStopAction | null }) =>
  isDropStop(stop) || isHookStop(stop)

const isBobtailStop = (stop: { scosAction?: ScosStopAction | null }): boolean => {
  return (
    !!stop.scosAction &&
    [
      ScosStopAction.Bbt,
      ScosStopAction.Ibbt,
      ScosStopAction.Ebt,
      ScosStopAction.Iebt,
    ].includes(stop.scosAction)
  )
}

/**
 * Returns the boundary stops of the trailer segments immediately before and after
 * the given segment within a leg.
 */
const getAdjacentTrailerSegmentStops = <TStop extends Pick<Stop, 'trailerSegmentIdx'>>(
  leg: { stops: Array<TStop> },
  trailerSegmentIdx: number | null | undefined
): { previousStop: TStop | undefined; nextStop: TStop | undefined } => {
  if (!trailerSegmentIdx) {
    return { previousStop: undefined, nextStop: undefined }
  }

  const previousStop =
    trailerSegmentIdx > 1
      ? leg.stops
          .filter((stop) => stop.trailerSegmentIdx === trailerSegmentIdx - 1)
          .at(-1)
      : undefined
  const nextStop = leg.stops.find(
    (stop) => stop.trailerSegmentIdx === trailerSegmentIdx + 1
  )

  return { previousStop, nextStop }
}

const isTrailerOnAdjacentSegment = <
  TStop extends Pick<Stop, 'trailerSegmentIdx' | 'trailer'>
>(
  leg: { stops: Array<TStop> },
  trailerSegmentIdx: number | null | undefined,
  trailerId: number
): boolean => {
  const { previousStop, nextStop } = getAdjacentTrailerSegmentStops(
    leg,
    trailerSegmentIdx
  )

  return previousStop?.trailer?.id === trailerId || nextStop?.trailer?.id === trailerId
}

const isCrossLegTrailerChange = (
  leg: { idx: number },
  stop: Pick<Stop, 'trailerSegmentIdx'>
): boolean => {
  return leg.idx !== 1 && stop.trailerSegmentIdx === 1
}

export {
  calculateMileageForStop,
  canStopBeAdded,
  formatTime,
  getApptTime,
  getAutoDispatchEligible,
  getFormattedTime,
  getAdjacentTrailerSegmentStops,
  isTrailerOnAdjacentSegment,
  getStopStatus,
  getStopTimeZone,
  getStopFreightStatus,
  getStopsWithReorderStatusRadiusV2,
  getStopsWithReorderStatusRadiusV3,
  getTableStyles,
  getWeekDateRange,
  isLegPlannedStartTimeEditable,
  isBobtailStop,
  isCrossLegTrailerChange,
  isDropStop,
  isDropOrHookStop,
  isHookStop,
  transformData,
  validateAppointmentTime,
  getSuggestedAppointmentWindow,
  isAppointmentTimeChanged,
  getCurrentOrNextStop,
  getTrackingData,
  isExpirationDateBeforePlannedEnd,
  extractLocationInfo,
  getRemainingTime,
  sortTractorsByAvailability,
  getLegsWithoutDriverIndices,
  filterOutSkippedStops,
  findFirstMatchingValue,
  isTractorUnavailable,
  getTotalArrivedStopsForLeg,
  getImmediateDispatchReason,
  willLoadBeImmediatelyDispatched,
  mergeDetailedLoadCore,
}
